/*
 * Network Status Hook
 * 
 * Custom React hook to monitor internet connectivity in real-time
 * 
 * Features:
 * - Detects online/offline status changes
 * - Real-time connectivity monitoring
 * - Cross-browser compatibility
 * - Provides network status state
 * 
 * Usage:
 * const { isOnline, isOffline } = useNetworkStatus();
 */

import { useState, useEffect } from 'react';

/**
 * Custom hook to monitor network connectivity status
 * @returns {Object} Network status information
 */
export const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (wasOffline) {
        // Small delay to ensure connection is stable
        setTimeout(() => {
          setWasOffline(false);
        }, 1000);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
    };

    // Add event listeners for network status changes
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Additional connectivity checks for more reliable detection
    const checkConnectivity = async () => {
      try {
        // Try to fetch a small resource to verify actual connectivity
        const response = await fetch('/favicon.ico', {
          method: 'HEAD',
          cache: 'no-cache',
          timeout: 5000
        });
        
        if (response.ok && !isOnline) {
          handleOnline();
        }
      } catch (error) {
        if (isOnline) {
          handleOffline();
        }
      }
    };

    // Periodic connectivity check every 30 seconds
    const connectivityInterval = setInterval(checkConnectivity, 30000);

    // Cleanup event listeners and interval
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(connectivityInterval);
    };
  }, [isOnline, wasOffline]);

  return {
    isOnline,
    isOffline: !isOnline,
    wasOffline,
    networkStatus: isOnline ? 'online' : 'offline'
  };
};